import React, {useMemo} from 'react';
import {ActivityIndicator, StyleSheet, View} from 'react-native';
import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import Icon from '../components/management/SafeIcon';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import PushNotificationsBootstrap from '../components/PushNotificationsBootstrap';
import ManagementDashboardEntryScreen from './ManagementDashboardEntryScreen.js';
import ManagementDataScreen from './ManagementDataScreen.js';
import CalendarScreen from './CalendarScreen.js';
import ManagementSettingsScreen from './ManagementSettingsScreen.js';
import MailHomeScreen from './mail/MailHomeScreen.js';
import {useManagementTheme} from '../helpers/managementTheme';
import {useManagementAccess} from '../helpers/managementAccess';
import {useMail} from '../helpers/MailContext';
import {EmptyState} from '../components/management/ManagementUI';
import {useLastTab} from '../helpers/useLastTab';
import {PREMIUM_TAB_SCREEN_OPTIONS, renderPremiumTabBar} from '../components/premium/PremiumTabBar';
import {visibleManagementTabs, resolveManagementTabLabel} from '../config/managementTabs';

const Tab = createBottomTabNavigator();

// Route-name → screen component map. Tab order, labels, icons and gating come
// from the shared MANAGEMENT_TABS metadata; only the heavy components live here.
// Finance & Service are NOT tabs anymore — they are stack routes
// (ManagementFinanceScreen / ManagementServiceScreen) opened from the Modules hub.
const TAB_COMPONENTS = {
    ManagementDashboard: ManagementDashboardEntryScreen,
    ManagementData: ManagementDataScreen,
    ManagementMail: MailHomeScreen,
    ManagementCalendar: CalendarScreen,
    ManagementSettings: ManagementSettingsScreen,
};

export default function ManagementHomeScreen() {
    const { t } = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const {loading: accessLoading, canAccess} = useManagementAccess();
    // App-wide mailbox state (provided by AppProviders). The Mail tab only
    // appears once the user has a configured email account, and carries a live
    // unread badge sourced from the same status the universal menu badge reads.
    const {configured: mailConfigured, unreadCount: mailUnread} = useMail();

    const tabs = useMemo(() => visibleManagementTabs({canAccess, mailConfigured}).map((tab) => ({
        name: tab.name,
        component: TAB_COMPONENTS[tab.name],
        label: resolveManagementTabLabel(t, tab),
        icon: tab.icon,
        // Live unread badge on the Mail tab (mirrors the universal menu badge).
        badge: tab.name === 'ManagementMail' && mailUnread > 0
            ? (mailUnread > 99 ? '99+' : mailUnread)
            : undefined,
    })), [canAccess, t, mailConfigured, mailUnread]);

    // Remember the last active tab across app restarts (validated against the
    // currently-available, access-filtered tab set).
    const tabNames = useMemo(() => tabs.map((tab) => tab.name), [tabs]);
    const {ready: lastTabReady, initialRouteName: lastTab, screenListeners: tabListeners} = useLastTab('management', {
        fallback: tabs[0]?.name || 'ManagementDashboard',
        valid: tabNames,
        // A user-pinned "Main screen" (Management → Settings) wins over last-tab memory.
        preferPinned: true,
        // The tab allow-list is empty until management access resolves; defer the
        // pin/last-tab resolution until then so the pinned "Main screen" isn't
        // validated against — and dropped by — the still-loading (empty) list.
        pending: accessLoading,
    });


    if (accessLoading || !lastTabReady) {
        return <View style={[styles.center, {backgroundColor: m.colors.bg}]}><ActivityIndicator size="large" color={m.colors.primary} /></View>;
    }

    if (tabs.length === 0) {
        return (
            <View style={[styles.center, {backgroundColor: m.colors.bg, padding: 20}]}>
                <EmptyState
                    icon="lock-alert-outline"
                    title={t('mgmt_home_no_menu_title')}
                    text={t('mgmt_home_no_menu_text')}
                />
            </View>
        );
    }

    return (
        <>
            <PushNotificationsBootstrap navigation={navigation} />
            <Tab.Navigator
                initialRouteName={lastTab || tabs[0]?.name || 'ManagementDashboard'}
                screenListeners={tabListeners}
                tabBar={renderPremiumTabBar}
                screenOptions={{...PREMIUM_TAB_SCREEN_OPTIONS, sceneStyle: {backgroundColor: m.colors.bg}}}
                sceneContainerStyle={{backgroundColor: m.colors.bg}}
            >
                {tabs.map((tab) => (
                    <Tab.Screen
                        key={tab.name}
                        name={tab.name}
                        component={tab.component}
                        options={{
                            tabBarLabel: tab.label,
                            tabBarIcon: ({ color, size }) => <Icon name={tab.icon} color={color} size={size} />,
                            ...(tab.badge != null ? {tabBarBadge: tab.badge} : {}),
                        }}
                    />
                ))}
            </Tab.Navigator>
        </>
    );
}

const styles = StyleSheet.create({
    center: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});



